import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaArrowUp, FaArrowDown, FaInfoCircle } from 'react-icons/fa';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

function DrillDown({ isOpen, onClose, metric, data }) {
  if (!metric) return null;

  const values = data.map(d => d[metric.key]).filter(v => v !== null && v !== undefined);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const avgValue = (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2);
  const latest = values[values.length - 1];
  const previous = values[values.length - 2];
  const change = previous ? ((latest - previous) / previous * 100).toFixed(1) : null;
  const overallChange = values.length > 1 ? ((latest - values[0]) / values[0] * 100).toFixed(1) : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl z-50 overflow-y-auto"
          >
            {/* Header */}
            <div
              className="p-6 text-white"
              style={{ backgroundColor: metric.color }}
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold">{metric.label}</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition"
                >
                  <FaTimes />
                </button>
              </div>
              <p className="text-white text-opacity-80 text-sm">{metric.description}</p>
            </div>

            <div className="p-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <SummaryCard
                  label="Latest Value"
                  value={`${latest}${metric.unit || ''}`}
                  color={metric.color}
                />
                <SummaryCard
                  label="Year on Year"
                  value={change ? `${change > 0 ? '+' : ''}${change}%` : 'N/A'}
                  color={change > 0 ? (metric.higherIsBetter ? '#10B981' : '#EF4444') : (metric.higherIsBetter ? '#EF4444' : '#10B981')}
                />
                <SummaryCard
                  label="Average"
                  value={`${avgValue}${metric.unit || ''}`}
                  color="#6B7280"
                />
                <SummaryCard
                  label="Overall Trend"
                  value={overallChange ? `${overallChange > 0 ? '+' : ''}${overallChange}%` : 'N/A'}
                  color={overallChange > 0 ? (metric.higherIsBetter ? '#10B981' : '#EF4444') : (metric.higherIsBetter ? '#EF4444' : '#10B981')}
                />
              </div>

              {/* Chart */}
              <div className="bg-gray-50 rounded-xl p-4 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Historical Trend
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.filter(d => d[metric.key] !== null)}>
                    <defs>
                      <linearGradient id="drillGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={metric.color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={metric.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                      formatter={(value) => [`${value}${metric.unit || ''}`, metric.label]}
                    />
                    <Area
                      type="monotone"
                      dataKey={metric.key}
                      stroke={metric.color}
                      strokeWidth={3}
                      fill="url(#drillGradient)"
                      dot={{ fill: metric.color, r: 5 }}
                      activeDot={{ r: 8 }}
                      name={metric.label}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Min/Max */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <FaArrowUp className="text-green-600" />
                    <span className="text-sm font-medium text-green-700">Highest Value</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700">
                    {maxValue}{metric.unit || ''}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {data.find(d => d[metric.key] === maxValue)?.year}
                  </p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <FaArrowDown className="text-red-600" />
                    <span className="text-sm font-medium text-red-700">Lowest Value</span>
                  </div>
                  <p className="text-2xl font-bold text-red-700">
                    {minValue}{metric.unit || ''}
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    {data.find(d => d[metric.key] === minValue)?.year}
                  </p>
                </div>
              </div>

              {/* Data Table */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Year by Year Data</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white">
                        <th className="px-4 py-2 text-left text-gray-600">Year</th>
                        <th className="px-4 py-2 text-left text-gray-600">Value</th>
                        <th className="px-4 py-2 text-left text-gray-600">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data].reverse().map((row, index, arr) => {
                        const current = row[metric.key];
                        const prev = arr[index + 1]?.[metric.key];
                        const rowChange = prev && current ? ((current - prev) / prev * 100).toFixed(1) : null;
                        const isPositiveChange = rowChange > 0;

                        return (
                          <tr key={index} className="border-t hover:bg-white transition">
                            <td className="px-4 py-2 font-semibold" style={{ color: metric.color }}>
                              {row.year}
                            </td>
                            <td className="px-4 py-2 font-medium">
                              {current !== null && current !== undefined
                                ? `${current}${metric.unit || ''}`
                                : 'N/A'}
                            </td>
                            <td className="px-4 py-2">
                              {rowChange && (
                                <span className={`text-sm font-medium flex items-center gap-1 ${
                                  isPositiveChange
                                    ? (metric.higherIsBetter ? 'text-green-600' : 'text-red-600')
                                    : (metric.higherIsBetter ? 'text-red-600' : 'text-green-600')
                                }`}>
                                  {isPositiveChange ? <FaArrowUp /> : <FaArrowDown />}
                                  {isPositiveChange ? '+' : ''}{rowChange}%
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Info */}
              {metric.info && (
                <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded flex gap-3">
                  <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{metric.info}</p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-200">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

export default DrillDown;